import { Checks, ChartBar } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { Message } from "../../../models/Message";
import { normalizePollOptions, parsePollMessage } from "../../../utils/specialMessages";
import { PollView } from "./styles";

export interface PollProps {
  message: Message;
};

const getStoredVotes = ( key: string ) => {
  try {
    const value = window.localStorage.getItem( key );
    const parsed = JSON.parse( value || "[]" );

    return Array.isArray( parsed ) ? parsed.filter(( item ) => typeof item === "number" ) : [];
  } catch {
    return [];
  }
};

export const Poll = ({ message }: PollProps ) => {
  const messageSnapshot = useMemo(() => ({
    id: message.id,
    content: message.content,
    messageType: message.messageType,
  } as Message ), [ message.id, message.content, message.messageType ]);
  const poll = useMemo(() => parsePollMessage( messageSnapshot ), [ messageSnapshot ]);
  const options = useMemo(() => normalizePollOptions( poll ), [ poll ]);
  const selectableCount = Math.max( 1, poll?.selectableOptionCount || 1 );
  const storageKey = `void-chat:poll-votes:${ message.id }`;
  const [ selectedIndexes, setSelectedIndexes ] = useState<number[]>([]);

  useEffect(() => {
    const storedVotes = getStoredVotes( storageKey );
    const selectedFromPayload = options
      .map(( option, index ) => option.selected ? index : -1 )
      .filter(( index ) => index >= 0 );

    setSelectedIndexes( storedVotes.length > 0 ? storedVotes : selectedFromPayload );
  }, [ message.id, options, storageKey ]);

  useEffect(() => {
    try {
      if( selectedIndexes.length === 0 ) {
        window.localStorage.removeItem( storageKey );
        return;
      }

      window.localStorage.setItem( storageKey, JSON.stringify( selectedIndexes ));
    } catch {
      // Voting is a local UI enhancement until the backend exposes poll votes.
    }
  }, [ selectedIndexes, storageKey ]);

  const computedOptions = options.map(( option, index ) => {
    const isSelected = selectedIndexes.includes( index );
    const localVote = isSelected && !option.selected ? 1 : 0;
    return {
      ...option,
      isSelected,
      votes: ( option.votes || 0 ) + localVote,
    };
  });
  const totalVotes = computedOptions.reduce(( total, option ) => total + option.votes, 0 );

  const handleSelect = ( index: number ) => {
    setSelectedIndexes(( current ) => {
      if( selectableCount === 1 ) return [ index ];
      if( current.includes( index )) return current.filter(( item ) => item !== index );
      if( current.length >= selectableCount ) return [ ...current.slice( 1 ), index ];
      return [ ...current, index ];
    });
  };

  return (
    <PollView>
      <div className="poll-card">
        <div className="poll-head">
          <div className="poll-icon">
            <Checks
              size={ 26 }
              weight="duotone"
            />
          </div>

          <div className="poll-title">
            <span>Enquete</span>
            <strong>{ poll?.title || "Escolha uma opção" }</strong>
          </div>
        </div>

        <div className="poll-options">
          {
            computedOptions.length > 0 ? computedOptions.map(( option, index ) => {
              const percentage = totalVotes > 0 ? Math.round(( option.votes / totalVotes ) * 100 ) : 0;

              return (
                <button
                  key={ `${ option.label }-${ index }` }
                  type="button"
                  className={ option.isSelected ? "selected" : "" }
                  aria-pressed={ option.isSelected }
                  onClick={( event ) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleSelect( index );
                  }}
                >
                  <span
                    className="poll-progress"
                    style={{ width: `${ percentage }%` }}
                  />

                  <span className="poll-option-label">
                    <span className="poll-radio" />
                    { option.label }
                  </span>

                  <span className="poll-percent">{ percentage }%</span>
                </button>
              );
            }) : <span className="poll-empty">Enquete sem opções disponíveis</span>
          }
        </div>

        <div className="poll-foot">
          <span>
            <ChartBar
              size={ 14 }
              weight="duotone"
            />
            { totalVotes } voto{ totalVotes === 1 ? "" : "s" }
          </span>

          <span>{ selectableCount > 1 ? `Escolha até ${ selectableCount } opções` : "Escolha única" }</span>
        </div>
      </div>
    </PollView>
  );
};
